import { useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  EditableToken,
  EditableSlotGroup,
  RichTextContent,
  useContentToken,
  useSlotGroup,
  usePropertyPhone,
  useTokens,
} from '@vibesites/runtime/react';
import { contrastRatio } from '../utils/luminance';

/**
 * A full-bleed white stripe used as breathing room / a visual break just above
 * the footer. Renders two side-by-side white cards: an "Apply Online Now" call
 * to action on the left and a "Need help? Call Us" phone panel on the right.
 * Still ships with the optional (empty by default) caption at the top.
 */
const applyCtaDef: SlotGroupDefinition = {
  label: 'Apply Now Button',
  fields: {
    url: { label: 'URL', type: 'url', defaultField: 'property_apply_now_url' },
    label: { label: 'Button Text', type: 'text', defaultValue: 'APPLY NOW' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

interface WhiteStripeProps {
  /**
   * When true, renders the caption + Apply / Call-us cards. When false
   * (the default), only the empty white stripe band is rendered. Content
   * is only shown on the Apply Now page.
   */
  showContent?: boolean;
}

export function WhiteStripe({ showContent = false }: WhiteStripeProps) {
  const helpPhoneToken = useContentToken('whitestripe.help_phone');
  const trackedPhone = usePropertyPhone();
  const helpPhone = helpPhoneToken || trackedPhone;
  const applyCta = useSlotGroup('whitestripe.apply_cta', applyCtaDef);

  // Match the resident-access card treatment: outline CTA that fills with the
  // accent on hover. When the property's primary fails WCAG AA contrast against
  // the pale card background, fall back to the dark text token so the CTA stays
  // legible — same luminance picker ResidentAccess uses.
  const tokens = useTokens();
  const [ctaAccent, ctaOnAccent] = useMemo(() => {
    const primary = tokens?.color?.primary;
    const background = tokens?.color?.background || '#ffffff';
    if (!primary) return ['var(--vibe-color-primary)', 'var(--vibe-color-text-on-primary)'] as const;
    return contrastRatio(primary, background) >= 4.5
      ? ['var(--vibe-color-primary)', 'var(--vibe-color-text-on-primary)'] as const
      : ['var(--vibe-color-text)', 'var(--vibe-color-background)'] as const;
  }, [tokens]);

  return (
    <section
      className="white-stripe-section"
      style={{
        ['--rise-ws-cta-accent' as string]: ctaAccent,
        ['--rise-ws-cta-on-accent' as string]: ctaOnAccent,
      }}
    >
      <div className="white-stripe">
        {showContent && (
          <>
            <RichTextContent
              path="whitestripe.caption"
              as="p"
              className="white-stripe__caption"
              defaultValue=""
            />

            <div className="white-stripe__cards">
              <div className="white-stripe__card">
                <div className="white-stripe__icon-wrap">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm0 2.5L17.5 8H14V4.5zM8 13h8v2H8v-2zm0 4h8v2H8v-2z" fill="currentColor"/>
                  </svg>
                </div>

                <RichTextContent
                  path="whitestripe.apply_heading"
                  as="h2"
                  className="white-stripe__heading"
                  defaultValue="Apply Online Now"
                />

                <RichTextContent
                  path="whitestripe.apply_body"
                  as="div"
                  className="white-stripe__body"
                  defaultValue={
                    '<p>100% safe and secure<br>Thousands apply online every day<br>Get the apartment you want right away!</p>'
                  }
                />

                <a
                  className="white-stripe__button"
                  href={applyCta.url || '#'}
                  target={applyCta._target || '_self'}
                  rel="noopener noreferrer"
                >
                  <EditableSlotGroup
                    groupPath="whitestripe.apply_cta"
                    definition={applyCtaDef}
                  >
                    <span className="white-stripe__button-label">
                      <span>{applyCta.label || 'APPLY NOW'}</span>
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M6 3l5 5-5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </span>
                  </EditableSlotGroup>
                </a>
              </div>

              <div className="white-stripe__card">
                <div className="white-stripe__icon-wrap">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                    <path d="M6.62 10.79a15.05 15.05 0 006.59 6.59l2.2-2.2a1 1 0 011.01-.24c1.12.37 2.33.57 3.58.57a1 1 0 011 1V20a1 1 0 01-1 1A17 17 0 013 4a1 1 0 011-1h3.5a1 1 0 011 1c0 1.25.2 2.46.57 3.58a1 1 0 01-.24 1.01l-2.21 2.2z" fill="currentColor"/>
                  </svg>
                </div>

                <RichTextContent
                  path="whitestripe.help_heading"
                  as="h2"
                  className="white-stripe__heading"
                  defaultValue="Need help? Call Us"
                />

                <a
                  href={helpPhone ? `tel:${helpPhone}` : '#'}
                  className="dniphone dniphonehref white-stripe__button"
                >
                  <EditableToken
                    path="whitestripe.help_phone"
                    as="span"
                    className="white-stripe__button-label"
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M4.64 1.33A.67.67 0 015.33 1H7a.67.67 0 01.64.47l.53 1.6a.67.67 0 01-.17.69L6.88 4.88a8 8 0 003.24 3.24l1.12-1.12a.67.67 0 01.69-.17l1.6.53A.67.67 0 0114 8v1.67a.67.67 0 01-.67.66A10 10 0 013.33 1.67.67.67 0 014 1h.64z" fill="currentColor"/></svg>
                    <span>{helpPhone}</span>
                  </EditableToken>
                </a>
              </div>
            </div>
          </>
        )}
      </div>

      <style>{`
        /* Fine diagonal hairline texture matching the supplied reference image,
           embedded as a tiling inline-SVG so it stays crisp at any size and
           needs no external request. Renders on every interior page in place
           of the old flat white band. */
        .white-stripe-section {
          background-color: #ffffff;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='8' height='8'%3E%3Cpath d='M0 8 L8 0' stroke='%23d5d8dd' stroke-width='0.6'/%3E%3C/svg%3E");
          background-size: 8px 8px;
          background-repeat: repeat;
        }

        .white-stripe {
          width: 100%;
          background: transparent;
          padding: 3rem 1.5rem;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 2rem;
        }

        .white-stripe__caption {
          margin: 0;
          text-align: center;
          font-family: var(--vibe-font-body);
          color: var(--vibe-color-text);
        }

        /* Collapse to no vertical contribution when there is no caption */
        .white-stripe__caption:empty {
          display: none;
        }

        .white-stripe__cards {
          width: 100%;
          max-width: 56rem;
          display: grid;
          grid-template-columns: 1fr;
          gap: 1.5rem;
        }
        @media (min-width: 768px) {
          .white-stripe__cards { grid-template-columns: 1fr 1fr; }
        }

        .white-stripe__card {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          padding: 2.5rem 2rem;
          background: #f7f7f8;
          border: 1px solid #e8e8ea;
          border-radius: var(--vibe-radius-lg, 12px);
          transition: box-shadow 0.25s, transform 0.25s;
        }
        .white-stripe__card:hover {
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06);
          transform: translateY(-2px);
        }

        .white-stripe__icon-wrap {
          width: 56px; height: 56px;
          display: flex; align-items: center; justify-content: center;
          border-radius: 14px;
          margin-bottom: 1.25rem;
          background: color-mix(in srgb, var(--vibe-color-primary) 12%, transparent);
          color: var(--vibe-color-primary);
        }

        .white-stripe__heading {
          margin: 0 0 0.75rem;
          font-family: var(--vibe-font-heading);
          font-size: 1.25rem;
          font-weight: 600;
          color: var(--vibe-color-text);
        }

        .white-stripe__body {
          font-family: var(--vibe-font-body);
          font-size: 0.875rem;
          line-height: 1.65;
          color: var(--vibe-color-text-muted);
          margin: 0 0 1.5rem;
          max-width: 22rem;
        }

        .white-stripe__body p {
          margin: 0;
        }

        /* Outline CTA matching ResidentAccess: transparent body, accent border +
           label, fills with the accent on hover. The accent is luminance-picked
           (--rise-ws-cta-accent) so pale primaries fall back to text. */
        .white-stripe__button {
          position: relative;
          overflow: hidden;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-family: var(--vibe-font-body);
          font-size: 0.8rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.2em;
          padding: 0.875rem 2rem;
          border-radius: 2px;
          text-decoration: none;
          cursor: pointer;
          background: transparent;
          border: 1.5px solid var(--rise-ws-cta-accent, var(--vibe-color-primary));
          color: var(--rise-ws-cta-accent, var(--vibe-color-primary));
          box-shadow: none;
          transition: color 0.3s ease, transform 0.3s ease, box-shadow 0.3s ease;
          margin-top: auto;
        }
        .white-stripe__button::before {
          content: '';
          position: absolute;
          inset: 0;
          background: var(--rise-ws-cta-accent, var(--vibe-color-primary));
          opacity: 0;
          transition: opacity 0.3s ease;
          z-index: 0;
        }
        .white-stripe__button-label {
          position: relative;
          z-index: 1;
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
        }
        .white-stripe__button:hover {
          color: var(--rise-ws-cta-on-accent, var(--vibe-color-text-on-primary));
          transform: translateY(-2px);
          box-shadow: 0 4px 16px color-mix(in srgb, var(--rise-ws-cta-accent, var(--vibe-color-primary)) 30%, transparent);
        }
        .white-stripe__button:hover::before { opacity: 1; }
        .white-stripe__button:active {
          transform: translateY(0);
          box-shadow: none;
        }

        @media (max-width: 640px) {
          .white-stripe__cards {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'WhiteStripe',
  displayName: 'White Stripe',
  tokens: {
    'whitestripe.caption': {
      label: 'Caption (optional)',
      type: 'richtext',
      defaultValue: '',
    },
    'whitestripe.apply_heading': {
      label: 'Apply Heading',
      type: 'richtext',
      defaultValue: 'Apply Online Now',
    },
    'whitestripe.apply_body': {
      label: 'Apply Body Copy',
      type: 'richtext',
      defaultValue:
        '<p>100% safe and secure<br>Thousands apply online every day<br>Get the apartment you want right away!</p>',
    },
    'whitestripe.help_heading': {
      label: 'Help Heading',
      type: 'richtext',
      defaultValue: 'Need help? Call Us',
    },
    'whitestripe.help_phone': {
      label: 'Help Phone',
      type: 'phone',
      defaultField: 'property_call_tracking_phone',
      suggestedFields: ['property_call_tracking_phone', 'property_phone'],
    },
  },
  slotGroups: {
    'whitestripe.apply_cta': applyCtaDef,
  },
};

(WhiteStripe as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default WhiteStripe;
